<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { vMaska } from 'maska/vue';
  import { Calendar } from 'lucide-vue-next';
  import { DatePicker } from '@/shared/ui';
  import { MASK_PRESETS } from '@/shared/lib/mask';
  import { formatCivilDateToPtBR, formatDateForApi, parseCivilDate } from '@/shared/lib';
  import type { CompiledFieldNode, FieldValue } from '@record-fill';
  import type { DateInputProperties } from '../../../model/form.types';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const control = props.node.control.kind === 'date' ? props.node.control : null;
  const isPickerOpen = ref(false);
  const defaultToToday =
    (props.node.element.properties as DateInputProperties).fieldTypes?.includes('present') ?? false;
  const maskConfig = { mask: MASK_PRESETS.date, eager: false };

  const civilValue = computed(() => (typeof props.node.value === 'string' ? props.node.value : ''));
  const selectedDate = computed(() => parseCivilDate(civilValue.value));
  const displayValue = computed(() =>
    selectedDate.value ? formatCivilDateToPtBR(selectedDate.value) : civilValue.value,
  );

  // The picker works with local Dates; the blob keeps the civil string, so the conversion uses
  // local calendar parts on both sides and never an ISO instant (DATE_CONVENTIONS.md).
  function toCivil(day: number, month: number, year: number): string | null {
    const date = new Date(year, month - 1, day);
    const isRealDay = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return isRealDay ? formatDateForApi(date) : null;
  }

  function onInput(event: Event): void {
    const typed = (event.target as HTMLInputElement).value;
    if (typed.trim().length === 0) {
      emit('update', '');
      return;
    }
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(typed);
    const civil = match ? toCivil(Number(match[1]), Number(match[2]), Number(match[3])) : null;
    emit('update', civil ?? typed);
  }

  function openPicker(): void {
    if (!props.disabled && !props.readOnly) isPickerOpen.value = true;
  }

  function onApply(date: Date | null): void {
    emit('update', date ? (formatDateForApi(date) ?? '') : '');
    isPickerOpen.value = false;
  }
</script>

<template>
  <div class="relative w-full min-w-0">
    <!-- Only the icon opens the picker: the modal takes the keystrokes, so opening it on a click in the input stops the person from typing. -->
    <input
      v-maska="maskConfig"
      type="text"
      :class="[
        inputClass,
        { 'cursor-default': readOnly },
        'w-full min-w-0 pr-8 text-[11px] leading-4 [font-family:var(--font-base)] placeholder:text-[10px] placeholder:opacity-60 disabled:cursor-not-allowed disabled:bg-[var(--color-gray-09)] disabled:opacity-60',
      ]"
      :value="displayValue"
      :placeholder="control?.placeholder ?? 'dd/mm/aaaa'"
      :disabled="disabled"
      :readonly="readOnly"
      :tabindex="readOnly ? -1 : undefined"
      :data-field-id="node.id"
      @input="onInput"
    >
    <button
      type="button"
      class="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center border-0 bg-transparent p-1 text-gray-500 transition-colors hover:enabled:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      :class="{ 'pointer-events-none': readOnly }"
      :disabled="disabled"
      :tabindex="readOnly ? -1 : undefined"
      :aria-hidden="readOnly || undefined"
      aria-label="Abrir calendário"
      @click="openPicker"
    >
      <Calendar :size="16" />
    </button>
    <DatePicker
      v-model:is-open="isPickerOpen"
      :selected-date="selectedDate"
      :disable-past-dates="false"
      :default-to-today="defaultToToday"
      @apply="onApply"
      @cancel="isPickerOpen = false"
    />
  </div>
</template>
